import {
  u2000FormatYmdFromParts,
  u2000GetActiveDisplayTimezone,
  u2000ReadWallClock,
  u2000ResolveTimezone,
  u2000SetActiveDisplayTimezone,
  u2000WallClockToUtcMs,
} from '../../u2000Core/tools/U2000DateTimeHelpers';

/** Met à jour le fuseau d'affichage actif (appelé depuis U3147Context au chargement initLU). */
export function u3147SetDisplayTimezone(timeZone: string | null | undefined): void {
  u2000SetActiveDisplayTimezone(timeZone);
}

export function u3147GetDisplayTimezone(): string {
  return u2000GetActiveDisplayTimezone();
}

/** Résout le fuseau demandé en repliant sur le fuseau actif org. */
function u3147ResolveDisplayTimezone(timeZone?: string | null): string {
  return u2000ResolveTimezone(timeZone ?? u2000GetActiveDisplayTimezone());
}

/** Formate des minutes en « Xh Ym » (affichage liste). */
export function u3147FormatMinutesAsHoursMinutes(totalMinutes: number | null | undefined): string {
  const m = typeof totalMinutes === 'number' && Number.isFinite(totalMinutes) ? Math.max(0, Math.round(totalMinutes)) : 0;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h <= 0) return `${min}m`;
  if (min <= 0) return `${h}h`;
  return `${h}h ${min}m`;
}

/** Formate des heures décimales (réponse day.totalHours). */
export function u3147FormatDecimalHours(hours: number | null | undefined): string {
  const h = typeof hours === 'number' && Number.isFinite(hours) ? hours : 0;
  return h.toFixed(2);
}

/** Extrait HH:MM depuis un ISO datetime dans le fuseau d'affichage org. */
export function u3147FormatTimeFromIso(iso: string | null | undefined, timeZone?: string): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = u2000ReadWallClock(d.getTime(), u3147ResolveDisplayTimezone(timeZone));
  return `${String(p.h).padStart(2, '0')}:${String(p.mi).padStart(2, '0')}`;
}

/** Compteur chrono HH:MM:SS depuis startedAt (ISO). */
export function u3147ElapsedSecondsFromStartedAt(startedAt: string | null | undefined, nowMs = Date.now()): number {
  if (!startedAt?.trim()) return 0;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return 0;
  return Math.max(0, Math.floor((nowMs - start) / 1000));
}

export function u3147FormatElapsedHms(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

/** Date ISO civile YYYY-MM-DD dans le fuseau d'affichage org. */
export function u3147TodayIsoDate(timeZone?: string): string {
  return u2000FormatYmdFromParts(u2000ReadWallClock(Date.now(), u3147ResolveDisplayTimezone(timeZone)));
}

/** Fin de journée métier (24:00) en minutes depuis minuit. */
export const U3147_WORK_DAY_END_MINUTES = 24 * 60;

/** Arrondit minutes depuis minuit sur l'horloge murale org au pas org (1, 5, 15, 30, 45 ou 60). */
export function u3147RoundMinutesFromMidnightToStep(minutesFromMidnight: number, stepMinutes: number): number {
  if (minutesFromMidnight <= 0) {
    return 0;
  }
  const step = stepMinutes > 0 ? stepMinutes : 1;
  return Math.round(minutesFromMidnight / step) * step;
}

/** Arrondit l'horloge murale (fuseau org) au pas en minutes — retourne un instant UTC. */
export function u3147RoundWallClockToStep(instant: Date, stepMinutes: number, timeZone?: string): Date {
  const tz = u3147ResolveDisplayTimezone(timeZone);
  const parts = u2000ReadWallClock(instant.getTime(), tz);
  const minutesFromMidnight = parts.h * 60 + parts.mi;
  const rounded = Math.min(
    U3147_WORK_DAY_END_MINUTES,
    u3147RoundMinutesFromMidnightToStep(minutesFromMidnight, stepMinutes),
  );
  const utcMs = u2000WallClockToUtcMs(
    {
      y: parts.y,
      mo: parts.mo,
      d: parts.d,
      h: Math.floor(rounded / 60),
      mi: rounded % 60,
      s: 0,
    },
    tz,
  );
  return new Date(utcMs);
}

function u3147RoundDurationMinutesClient(rawMinutes: number, stepMinutes: number): number {
  if (rawMinutes <= 0) return 0;
  const step = stepMinutes > 0 ? stepMinutes : 1;
  return Math.round(rawMinutes / step) * step;
}

/**
 * Clôture chrono : fin = début + durée arrondie au pas org (miroir backend u3147ComputeRoundedTimerEndedAt).
 */
export function u3147ComputeRoundedTimerEndIso(
  startedAtIso: string,
  stoppedAtMs = Date.now(),
  roundingMinutes: number,
  timeZone?: string,
): { endedAt: string; durationMinutes: number } {
  const startedAt = new Date(startedAtIso);
  const stoppedAt = new Date(stoppedAtMs);
  const step = roundingMinutes > 0 ? roundingMinutes : 1;
  const rawMinutes = Math.max(
    0,
    Math.floor((stoppedAt.getTime() - startedAt.getTime()) / 60_000),
  );
  let durationMinutes = u3147RoundDurationMinutesClient(rawMinutes, step);
  if (durationMinutes <= 0) {
    durationMinutes = step;
  }
  let endedAt = new Date(startedAt.getTime() + durationMinutes * 60_000);
  const workDate = u3147WorkDateFromInstantIso(startedAtIso, timeZone);
  if (workDate) {
    const dayEndMs = new Date(u3147WorkDayEndInstantIso(workDate, timeZone)).getTime();
    if (endedAt.getTime() > dayEndMs) {
      endedAt = new Date(dayEndMs);
      durationMinutes = Math.max(0, Math.floor((endedAt.getTime() - startedAt.getTime()) / 60_000));
    }
  }
  return { endedAt: u3147ToInstantIso(endedAt), durationMinutes };
}

/** Plages clôturées plus anciennes qui chevauchent [keptStart, keptEnd] (exclut keptSessionId). */
export function u3147ListOverlappingOlderSessions(
  keptStartIso: string,
  keptEndIso: string,
  candidates: U3147OverlapSessionLike[],
  keptSessionId: string,
): U3147OverlapSessionLike[] {
  const keptStart = new Date(keptStartIso).getTime();
  const keptEnd = new Date(keptEndIso).getTime();
  if (Number.isNaN(keptStart) || Number.isNaN(keptEnd) || keptEnd <= keptStart) {
    return [];
  }

  return candidates.filter((other) => {
    if (other.id === keptSessionId) return false;
    if (!other.startedAt?.trim() || !other.endedAt?.trim()) return false;
    const oStart = new Date(other.startedAt).getTime();
    const oEnd = new Date(other.endedAt).getTime();
    if (Number.isNaN(oStart) || Number.isNaN(oEnd)) return false;
    if (oStart >= keptStart) return false;
    return oStart < keptEnd && oEnd > keptStart;
  });
}

function u3147MinutesToHm(totalMinutes: number): string {
  const clamped = Math.min(Math.max(0, Math.round(totalMinutes)), U3147_WORK_DAY_END_MINUTES);
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Jour civil org (yyyy-MM-dd) depuis un instant ISO. */
export function u3147WorkDateFromInstantIso(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return u2000FormatYmdFromParts(u2000ReadWallClock(d.getTime(), u3147ResolveDisplayTimezone(timeZone)));
}

/** Instant exclusif de fin de jour civil org (24:00 = minuit jour suivant). */
export function u3147WorkDayEndInstantIso(workDate: string, timeZone?: string): string {
  const datePart = workDate.slice(0, 10);
  const nextDay = u3147ShiftIsoDate(datePart, 1);
  return u3147CombineDateAndTimeToIso(nextDay, '00:00', timeZone);
}

export function u3147IsHmWithinWorkDay(value: string): boolean {
  if (!u3147IsValidHm(value)) return false;
  return u3147HmToMinutes(value) <= U3147_WORK_DAY_END_MINUTES;
}

export function u3147WouldExceedWorkDayEnd(startTime: string, durationMinutes: number): boolean {
  if (!u3147IsValidHm(startTime) || !Number.isFinite(durationMinutes)) return true;
  return u3147HmToMinutes(startTime) + durationMinutes > U3147_WORK_DAY_END_MINUTES;
}

/** Fin ≤ début (même jour civil, heures HH:MM). */
export function u3147IsManualEndBeforeOrAtStart(startTime: string, endTime: string): boolean {
  if (!u3147IsValidHm(startTime) || !u3147IsValidHm(endTime)) return false;
  return u3147HmToMinutes(endTime) <= u3147HmToMinutes(startTime);
}

/** Champs à marquer en erreur si la plage dépasse 24:00 (jour civil). */
export function u3147ResolveWorkDayEndFieldFeedback(
  startTime: string,
  endTime: string,
  durationMinutes: string,
  useDurationMode: boolean,
): U3147ManualTimeField[] {
  if (!u3147IsValidHm(startTime)) return [];
  if (useDurationMode) {
    const mins = Number(durationMinutes.trim());
    if (!Number.isFinite(mins) || mins <= 0 || !Number.isInteger(mins)) return [];
    if (u3147WouldExceedWorkDayEnd(startTime, mins)) {
      return ['durationMinutes'];
    }
    return [];
  }
  if (endTime.trim() && /^\d{1,2}:\d{2}$/.test(endTime.trim())) {
    if (u3147HmToMinutes(endTime) > U3147_WORK_DAY_END_MINUTES) {
      return ['endTime'];
    }
  }
  if (u3147IsValidHm(endTime)) {
    if (u3147IsManualEndBeforeOrAtStart(startTime, endTime)) {
      return ['endTime'];
    }
    if (!u3147IsHmWithinWorkDay(endTime)) {
      return ['endTime'];
    }
  }
  return [];
}

/** Pré-remplit début/fin HH:MM pour une nouvelle plage (durée = roundingMinutes, fin = maintenant arrondi). */
export function u3147DefaultManualSessionRange(
  _workDate: string,
  roundingMinutes: number,
  timeZone?: string,
  nowMs = Date.now(),
): { startTime: string; endTime: string; durationMinutes: string } {
  const step = roundingMinutes > 0 ? roundingMinutes : 1;
  const roundedEnd = u3147RoundWallClockToStep(new Date(nowMs), step, timeZone);
  const tz = u3147ResolveDisplayTimezone(timeZone);
  const parts = u2000ReadWallClock(roundedEnd.getTime(), tz);
  const endMinutes = Math.min(U3147_WORK_DAY_END_MINUTES, parts.h * 60 + parts.mi);
  const startMinutes = Math.max(0, endMinutes - step);
  return {
    startTime: u3147MinutesToHm(startMinutes),
    endTime: u3147MinutesToHm(endMinutes),
    durationMinutes: String(endMinutes - startMinutes),
  };
}

/** Valeurs initiales dialogue « Ajouter une plage » (aujourd'hui org → BL-0106 ; autre jour → créneau libre). */
export function u3147BuildNewManualSessionDefaults(
  defaultDate: string,
  existingSessions: U3147OverlapSessionLike[],
  roundingMinutes: number,
  organizationTimezone?: string,
): { startTime: string; endTime: string; durationMinutes: string } {
  const datePart = defaultDate.slice(0, 10);
  const step = roundingMinutes > 0 ? roundingMinutes : 1;
  const durationStr = String(step);

  if (datePart === u3147TodayIsoDate(organizationTimezone)) {
    return u3147DefaultManualSessionRange(datePart, step, organizationTimezone);
  }

  const startTime = u3147ResolveNextGapStartHm(datePart, existingSessions);
  const endTime = u3147ComputeEndHmFromStartAndDuration(startTime, durationStr) ?? '09:00';
  return { startTime, endTime, durationMinutes: durationStr };
}

/** Décale une date ISO YYYY-MM-DD de delta jours. */
export function u3147ShiftIsoDate(isoDate: string, deltaDays: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Combine date YYYY-MM-DD + heure HH:MM → instant ISO UTC (Z). */
export function u3147CombineDateAndTimeToIso(workDate: string, timeHm: string, timeZone?: string): string {
  const datePart = workDate.slice(0, 10);
  const [hh, mm] = timeHm.split(':');
  const utcMs = u2000WallClockToUtcMs(
    {
      y: Number(datePart.slice(0, 4)),
      mo: Number(datePart.slice(5, 7)),
      d: Number(datePart.slice(8, 10)),
      h: Number(hh),
      mi: Number(mm),
      s: 0,
    },
    u3147ResolveDisplayTimezone(timeZone),
  );
  return new Date(utcMs).toISOString();
}

export function u3147IsValidHm(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d{1,2}:\d{2}$/.test(trimmed)) return false;
  const [hRaw, mRaw] = trimmed.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return false;
  if (m < 0 || m > 59) return false;
  if (h < 0 || h > 24) return false;
  if (h === 24 && m !== 0) return false;
  return true;
}

export function u3147HmToMinutes(value: string): number {
  const [h, m] = value.trim().split(':').map((x) => Number(x));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}

/** Instant ISO UTC (Z) à partir d'un Date. */
export function u3147ToInstantIso(date: Date): string {
  return date.toISOString();
}

/** Heure de fin d'une plage manuelle : saisie directe ou début + durée (minutes). */
export function u3147ResolveManualSessionEndIso(
  workDate: string,
  startTime: string,
  endTime: string,
  useDurationMode: boolean,
  durationMinutes: string,
): string | null {
  if (!u3147IsValidHm(startTime)) return null;
  const startedAt = u3147CombineDateAndTimeToIso(workDate, startTime);
  const start = new Date(startedAt);
  if (Number.isNaN(start.getTime())) return null;

  if (useDurationMode) {
    const mins = Number(durationMinutes.trim());
    if (!Number.isFinite(mins) || mins <= 0 || !Number.isInteger(mins)) return null;
    if (u3147WouldExceedWorkDayEnd(startTime, mins)) return null;
    return u3147ToInstantIso(new Date(start.getTime() + mins * 60_000));
  }

  if (!u3147IsValidHm(endTime) || !u3147IsHmWithinWorkDay(endTime)) return null;
  if (u3147HmToMinutes(endTime) <= u3147HmToMinutes(startTime)) return null;
  return u3147CombineDateAndTimeToIso(workDate, endTime);
}

/** Limite un instant de fin au 24:00 du jour civil org du début. */
export function u3147ClampEndedAtIsoToWorkDay(
  startedAtIso: string,
  endedAtIso: string,
  timeZone?: string,
): string {
  const workDate = u3147WorkDateFromInstantIso(startedAtIso, timeZone);
  if (!workDate) return endedAtIso;
  const dayEndMs = new Date(u3147WorkDayEndInstantIso(workDate, timeZone)).getTime();
  const endedMs = new Date(endedAtIso).getTime();
  if (Number.isNaN(endedMs) || endedMs <= dayEndMs) return endedAtIso;
  return u3147ToInstantIso(new Date(dayEndMs));
}

export function u3147ComputeSessionDurationMinutes(
  startedAt: string | undefined,
  endedAt: string | undefined,
  durationMinutes?: number | null,
): number {
  if (typeof durationMinutes === 'number' && durationMinutes > 0) {
    return Math.round(durationMinutes);
  }
  const start = startedAt ? new Date(startedAt).getTime() : Number.NaN;
  const end = endedAt ? new Date(endedAt).getTime() : Number.NaN;
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 60;
  return Math.max(1, Math.round((end - start) / 60_000));
}

/** Heure de fin (HH:MM, même jour) à partir du début et d'une durée en minutes. */
export function u3147ComputeEndHmFromStartAndDuration(startTime: string, durationMinutes: string): string | null {
  const mins = Number(durationMinutes.trim());
  if (!u3147IsValidHm(startTime) || !Number.isFinite(mins) || mins <= 0 || !Number.isInteger(mins)) return null;
  if (u3147WouldExceedWorkDayEnd(startTime, mins)) return null;
  return u3147MinutesToHm(u3147HmToMinutes(startTime) + mins);
}

export function u3147ComputeDurationMinutesFromStartEnd(startTime: string, endTime: string): string | null {
  if (!u3147IsValidHm(startTime) || !u3147IsValidHm(endTime) || !u3147IsHmWithinWorkDay(endTime)) return null;
  const mins = u3147HmToMinutes(endTime) - u3147HmToMinutes(startTime);
  if (mins <= 0) return null;
  return String(mins);
}

export type U3147ManualTimeField = 'startTime' | 'endTime' | 'durationMinutes';

const U3147_MANUAL_TIME_FIELD_ORDER: readonly U3147ManualTimeField[] = ['startTime', 'endTime', 'durationMinutes'];

export interface U3147OverlapSessionLike {
  id?: string;
  workDate?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
}

export type U3147OverlapHintKind =
  | 'startBeforeOrAfter'
  | 'startBefore'
  | 'startAfter'
  | 'endBefore'
  | 'endAfter'
  | 'endBeforeOrAfter';

export interface U3147OverlapFieldHint {
  kind: U3147OverlapHintKind;
  beforeTime?: string;
  afterTime?: string;
}

export interface U3147OverlapFieldFeedback {
  field: U3147ManualTimeField;
  hint: U3147OverlapFieldHint;
}

function u3147MergeOverlapHint(
  current: U3147OverlapFieldHint | undefined,
  next: U3147OverlapFieldHint,
): U3147OverlapFieldHint {
  if (!current) return next;
  const richKinds: U3147OverlapHintKind[] = ['startBeforeOrAfter', 'endBeforeOrAfter'];
  if (richKinds.includes(current.kind)) return current;
  if (richKinds.includes(next.kind)) return next;
  return next;
}

function u3147SetOverlapHint(
  hints: Partial<Record<U3147ManualTimeField, U3147OverlapFieldHint>>,
  field: U3147ManualTimeField,
  next: U3147OverlapFieldHint,
): void {
  hints[field] = u3147MergeOverlapHint(hints[field], next);
}

/** Indique quels champs sont en erreur et les créneaux horaires disponibles. */
export function u3147ResolveOverlapFieldFeedback(
  startedAt: string,
  endedAt: string,
  existingSessions: U3147OverlapSessionLike[],
  options: {
    excludeSessionId?: string;
    useDurationMode: boolean;
  },
): U3147OverlapFieldFeedback[] {
  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return [];

  const endField: U3147ManualTimeField = options.useDurationMode ? 'durationMinutes' : 'endTime';
  const hints: Partial<Record<U3147ManualTimeField, U3147OverlapFieldHint>> = {};

  for (const s of existingSessions) {
    if (options.excludeSessionId && s.id === options.excludeSessionId) continue;
    if (!s.startedAt || !s.endedAt) continue;
    const oStart = new Date(s.startedAt).getTime();
    const oEnd = new Date(s.endedAt).getTime();
    if (Number.isNaN(oStart) || Number.isNaN(oEnd)) continue;
    if (!(start < oEnd && end > oStart)) continue;

    const oStartHm = u3147FormatTimeFromIso(s.startedAt);
    const oEndHm = u3147FormatTimeFromIso(s.endedAt);
    const insideOther = start >= oStart && end <= oEnd;
    const engulfsOther = start <= oStart && end >= oEnd;

    if (insideOther || engulfsOther) {
      u3147SetOverlapHint(hints, 'startTime', {
        kind: 'startBeforeOrAfter',
        beforeTime: oStartHm,
        afterTime: oEndHm,
      });
      u3147SetOverlapHint(hints, endField, {
        kind: 'endBeforeOrAfter',
        beforeTime: oStartHm,
        afterTime: oEndHm,
      });
      continue;
    }

    if (start >= oStart && start < oEnd) {
      u3147SetOverlapHint(hints, 'startTime', {
        kind: 'startBeforeOrAfter',
        beforeTime: oStartHm,
        afterTime: oEndHm,
      });
    }
    if (end > oStart && end <= oEnd) {
      if (start < oStart) {
        u3147SetOverlapHint(hints, endField, { kind: 'endBefore', beforeTime: oStartHm });
      } else {
        u3147SetOverlapHint(hints, endField, {
          kind: 'endBeforeOrAfter',
          beforeTime: oStartHm,
          afterTime: oEndHm,
        });
      }
    }
    if (start < oStart && end > oStart) {
      u3147SetOverlapHint(hints, endField, { kind: 'endBefore', beforeTime: oStartHm });
    }
    if (end > oEnd && start < oEnd) {
      if (start >= oStart) {
        u3147SetOverlapHint(hints, 'startTime', { kind: 'startAfter', afterTime: oEndHm });
      } else {
        u3147SetOverlapHint(hints, 'startTime', {
          kind: 'startBeforeOrAfter',
          beforeTime: oStartHm,
          afterTime: oEndHm,
        });
      }
    }
  }

  return U3147_MANUAL_TIME_FIELD_ORDER.filter((field) => hints[field]).map((field) => ({
    field,
    hint: hints[field]!,
  }));
}

/** @deprecated Préférer u3147ResolveOverlapFieldFeedback */
export function u3147ResolveOverlapFaultFields(
  startedAt: string,
  endedAt: string,
  existingSessions: U3147OverlapSessionLike[],
  options: {
    excludeSessionId?: string;
    useDurationMode: boolean;
  },
): U3147ManualTimeField[] {
  return u3147ResolveOverlapFieldFeedback(startedAt, endedAt, existingSessions, options).map((item) => item.field);
}

/** Heure de début par défaut d'une journée de saisie. */
export const U3147_DAY_START_HM = '08:00';

/** Pause déjeuner exclue des créneaux libres (non enregistrée comme plage). */
export const U3147_LUNCH_START_HM = '12:00';
export const U3147_LUNCH_END_HM = '13:00';

function u3147LunchIntervalMs(workDate: string): { startMs: number; endMs: number } | null {
  const datePart = workDate?.slice(0, 10);
  if (!datePart) return null;
  const startMs = new Date(u3147CombineDateAndTimeToIso(datePart, U3147_LUNCH_START_HM)).getTime();
  const endMs = new Date(u3147CombineDateAndTimeToIso(datePart, U3147_LUNCH_END_HM)).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;
  return { startMs, endMs };
}

function u3147AdvanceCursorPastLunch(workDate: string, cursorMs: number): number {
  const lunch = u3147LunchIntervalMs(workDate);
  if (!lunch) return cursorMs;
  if (cursorMs >= lunch.startMs && cursorMs < lunch.endMs) {
    return lunch.endMs;
  }
  return cursorMs;
}

function u3147IsInsideLunchBreak(workDate: string, timeMs: number): boolean {
  const lunch = u3147LunchIntervalMs(workDate);
  if (!lunch) return false;
  return timeMs >= lunch.startMs && timeMs < lunch.endMs;
}

function u3147ListDaySessionIntervals(
  workDate: string,
  existingSessions: U3147OverlapSessionLike[],
  excludeSessionId?: string,
): { sessionStartMs: number; sessionEndMs: number }[] {
  const datePart = workDate?.slice(0, 10);
  if (!datePart) return [];

  return existingSessions
    .filter((s) => {
      if (excludeSessionId && s.id === excludeSessionId) return false;
      if (!s.startedAt?.trim()) return false;
      const sessionDate = s.workDate?.slice(0, 10) ?? s.startedAt.slice(0, 10);
      return sessionDate === datePart;
    })
    .map((s) => ({
      sessionStartMs: new Date(s.startedAt!).getTime(),
      sessionEndMs: s.endedAt ? new Date(s.endedAt).getTime() : Number.NaN,
    }))
    .filter((s) => !Number.isNaN(s.sessionStartMs))
    .sort((a, b) => a.sessionStartMs - b.sessionStartMs);
}

function u3147ListDayBlockedIntervals(
  workDate: string,
  existingSessions: U3147OverlapSessionLike[],
  excludeSessionId?: string,
): { startMs: number; endMs: number }[] {
  const sessions = u3147ListDaySessionIntervals(workDate, existingSessions, excludeSessionId)
    .filter((s) => !Number.isNaN(s.sessionEndMs))
    .map((s) => ({ startMs: s.sessionStartMs, endMs: s.sessionEndMs }));

  const lunch = u3147LunchIntervalMs(workDate);
  const blocked = lunch ? [...sessions, lunch] : sessions;
  return blocked.sort((a, b) => a.startMs - b.startMs);
}

function u3147MsToLocalHm(ms: number): string {
  return u3147FormatTimeFromIso(u3147ToInstantIso(new Date(ms)));
}

/** Début du prochain créneau libre à partir de 08:00 (ou après la dernière plage). */
export function u3147ResolveNextGapStartHm(
  workDate: string,
  existingSessions: U3147OverlapSessionLike[],
  options?: { excludeSessionId?: string; dayStartHm?: string },
): string {
  const dayStartHm = options?.dayStartHm ?? U3147_DAY_START_HM;
  const datePart = workDate?.slice(0, 10);
  if (!datePart || !u3147IsValidHm(dayStartHm)) return dayStartHm;

  const dayStartMs = new Date(u3147CombineDateAndTimeToIso(datePart, dayStartHm)).getTime();
  if (Number.isNaN(dayStartMs)) return dayStartHm;

  const blocked = u3147ListDayBlockedIntervals(workDate, existingSessions, options?.excludeSessionId);
  let cursorMs = u3147AdvanceCursorPastLunch(datePart, dayStartMs);

  for (const block of blocked) {
    if (block.endMs <= cursorMs) continue;
    cursorMs = u3147AdvanceCursorPastLunch(datePart, cursorMs);
    if (block.startMs > cursorMs) {
      return u3147MsToLocalHm(cursorMs);
    }
    cursorMs = u3147AdvanceCursorPastLunch(datePart, Math.max(cursorMs, block.endMs));
  }

  return u3147MsToLocalHm(cursorMs);
}

export interface U3147GapBeforeNextSession {
  gapDurationMinutes: number;
  nextStartHm: string;
  nextStartIso: string;
}

/** Créneau libre entre le début saisi et la prochaine plage du jour (début strictement avant). */
export function u3147ResolveGapBeforeNextSession(
  workDate: string,
  startTimeHm: string,
  existingSessions: U3147OverlapSessionLike[],
  excludeSessionId?: string,
): U3147GapBeforeNextSession | null {
  const datePart = workDate?.slice(0, 10);
  if (!datePart || !u3147IsValidHm(startTimeHm)) return null;

  const startIso = u3147CombineDateAndTimeToIso(datePart, startTimeHm);
  const startMs = new Date(startIso).getTime();
  if (Number.isNaN(startMs)) return null;

  const daySessions = u3147ListDaySessionIntervals(workDate, existingSessions, excludeSessionId);

  for (const s of daySessions) {
    if (!Number.isNaN(s.sessionEndMs) && startMs >= s.sessionStartMs && startMs < s.sessionEndMs) {
      return null;
    }
  }

  if (u3147IsInsideLunchBreak(datePart, startMs)) return null;

  const lunch = u3147LunchIntervalMs(datePart);
  const nextSession = daySessions.find((s) => s.sessionStartMs > startMs);
  if (!nextSession) return null;

  const nextBoundaryMs = nextSession.sessionStartMs;

  if (lunch && startMs < lunch.startMs) {
    if (nextBoundaryMs >= lunch.startMs) return null;
  } else if (lunch && startMs >= lunch.endMs) {
    if (nextBoundaryMs < lunch.endMs) return null;
  }

  const gapDurationMinutes = Math.floor((nextBoundaryMs - startMs) / 60_000);
  if (gapDurationMinutes <= 0) return null;

  const nextStartIso = u3147ToInstantIso(new Date(nextBoundaryMs));
  return {
    gapDurationMinutes,
    nextStartHm: u3147FormatTimeFromIso(nextStartIso),
    nextStartIso,
  };
}
